export const FEEDBACK_STORAGE_KEYS = Object.freeze({
  session: 'dha_nexus_feedback_session',
  failSave: 'dha_nexus_feedback_test_fail_save',
  failExport: 'dha_nexus_feedback_test_fail_export',
  failHistory: 'dha_nexus_feedback_test_fail_history',
});

export const FEEDBACK_CREATE_ACTIONS = Object.freeze([
  { id: 'text', label: 'Text box', testId: 'feedback-create-text' },
  { id: 'rectangle', label: 'Box', testId: 'feedback-create-rectangle' },
  { id: 'arrow', label: 'Arrow', testId: 'feedback-create-arrow' },
  { id: 'highlight', label: 'Highlight', testId: 'feedback-create-highlight' },
  { id: 'pin', label: 'Pin', testId: 'feedback-create-pin' },
  { id: 'pen', label: 'Pen', testId: 'feedback-create-pen' },
]);

export const FEEDBACK_TOOLS = Object.freeze([
  { id: 'select', label: 'Select/move', testId: 'feedback-tool-select', glyph: '↖' },
  { id: 'rectangle', label: 'Box', testId: 'feedback-tool-rectangle', glyph: '▭' },
  { id: 'arrow', label: 'Arrow', testId: 'feedback-tool-arrow', glyph: '↗' },
  { id: 'highlight', label: 'Highlight', testId: 'feedback-tool-highlight', glyph: '▰' },
  { id: 'pin', label: 'Numbered pin', testId: 'feedback-tool-pin', glyph: '①' },
  { id: 'text', label: 'Text callout', testId: 'feedback-tool-text', glyph: 'T' },
  { id: 'pen', label: 'Freehand pen', testId: 'feedback-tool-pen', glyph: '⌁' },
]);

export const FEEDBACK_COLORS = Object.freeze([
  { id: 'yellow', label: 'Yellow', value: '#F4B400' },
  { id: 'blue', label: 'Blue', value: '#2563eb' },
  { id: 'green', label: 'Green', value: '#15803d' },
  { id: 'red', label: 'Red', value: '#b42318' },
  { id: 'purple', label: 'Purple', value: '#7c3aed' },
  { id: 'gray', label: 'Gray', value: '#475569' },
]);

const LEGACY_FEEDBACK_COLORS = Object.freeze({
  amber: '#d97706',
  neutral: '#334155',
});

export const DEFAULT_FEEDBACK_COLOR = 'yellow';
export const DEFAULT_TEXT_BACKGROUND_MODE = 'white';

export const FEEDBACK_TEXT_BACKGROUND_MODES = Object.freeze([
  { id: 'white', label: 'White' },
  { id: 'transparent', label: 'Transparent' },
]);

export const FEEDBACK_TEXT_BOX_METRICS = Object.freeze({
  insetX: 16,
  baselineY: 32,
  fontSize: 24,
  lineHeight: 28,
});

export function colorValue(colorId) {
  if (!colorId) {
    return FEEDBACK_COLORS.find((color) => color.id === DEFAULT_FEEDBACK_COLOR)?.value || '#F4B400';
  }
  return (
    FEEDBACK_COLORS.find((color) => color.id === colorId)?.value ||
    LEGACY_FEEDBACK_COLORS[String(colorId).toLowerCase()] ||
    colorId
  );
}

export function colorIdForValue(value) {
  return FEEDBACK_COLORS.find(
    (color) =>
      color.id === String(value || '').toLowerCase() ||
      color.value.toLowerCase() === String(value || '').toLowerCase(),
  )?.id;
}

export const ANNOTATION_KIND_LABELS = Object.freeze({
  pin: 'Pin',
  rectangle: 'Box',
  arrow: 'Arrow',
  highlight: 'Highlight',
  text: 'Text',
  pen: 'Pen',
});

export function annotationKindLabel(type) {
  const raw = String(type ?? '').trim();
  const key = raw.toLowerCase();
  // Own-property check: bracket lookup on inherited keys ('__proto__',
  // 'constructor') would return non-string objects from a corrupt payload.
  if (Object.hasOwn(ANNOTATION_KIND_LABELS, key)) return ANNOTATION_KIND_LABELS[key];
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Mark';
}

export function colorLabelForValue(value) {
  const normalized = String(value || '').toLowerCase();
  if (!normalized) return 'Yellow';
  const paletteColor = FEEDBACK_COLORS.find(
    (color) => color.id === normalized || color.value.toLowerCase() === normalized,
  );
  if (paletteColor) return paletteColor.label;
  if (normalized === 'amber' || normalized === LEGACY_FEEDBACK_COLORS.amber) return 'Legacy amber';
  if (normalized === 'neutral' || normalized === LEGACY_FEEDBACK_COLORS.neutral)
    return 'Legacy neutral';
  return `Custom (${normalized})`;
}

export function normalizeTextBackgroundMode(mode) {
  return mode === 'transparent' ? 'transparent' : DEFAULT_TEXT_BACKGROUND_MODE;
}

export function textBackgroundFill(mode) {
  return normalizeTextBackgroundMode(mode) === 'transparent' ? 'transparent' : 'white';
}
